import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { missionFromRestV2, missionRouteFromRestV2 } from 'domain/mappers/missionFromRestV2';
import type { MissionFE, MissionRouteFE } from 'domain/mission';
import { client } from 'graphql/client';
import { onUpdateActiveMissionByVin } from 'graphql/subscriptions';
import { SafeMissionV2Schema } from 'helper/v2Schemas';

import { AppError, errorHandler, formatAxiosError, isAxiosError } from './errors';
import { applyMissionLiveUpdate, missionFromLiveV2 } from './mappers/missionLiveFieldsFromV2';
import { correlationHeaders, missionsApi } from './restClient';

import type { MissionV2 } from 'API';

/**
 * Mission queries run over REST; the live stream stays on AppSync. Both endpoints share this slice
 * because `util.updateQueryData` / `endpoints.x.initiate` are only type-safe within one `createApi`
 * instance — the same reason `vehiclesV2.ts` keeps its REST and GraphQL entries together.
 *
 * TODO REST-SUBSCRIPTIONS: `parseMission` throws inside `next` on a bad payload, so the update is
 * dropped silently and the screen keeps showing the previous mission. Switch to `safeParse`, patch
 * only on success, and surface staleness to the UI.
 */

const extractResult = <T>(
    result: { __typename?: string; errorCode?: string; statusCode?: number } | null | undefined,
    notFoundMessage: string
): T => {
    const isError = !result || result.__typename === 'ErrorResponse' || result.errorCode !== undefined;
    if (isError) {
        throw new AppError(result?.errorCode ?? notFoundMessage, {
            errorCode: result?.errorCode,
            statusCode: result?.statusCode,
        });
    }
    return result as unknown as T;
};

/** Parses an AppSync push through `SafeMissionV2Schema` for graceful degradation. */
const parseMission = (data: MissionV2) => SafeMissionV2Schema().parse(data);

/**
 * `GET /api/v2/missions/active/route/{vin}`. A module function, not an endpoint: every caller is in
 * this file. `null` means 204 (no active mission, or no generated route), checked on `status` before
 * `response.data` for the reason on {@link getActiveMissionQueryFn}.
 */
const getActiveMissionRoute = async (vin: string): Promise<MissionRouteFE | null> => {
    try {
        const response = await missionsApi.getActiveMissionRouteByVin(vin, correlationHeaders());
        if (response.status === 204) {
            return null;
        }
        return missionRouteFromRestV2(response.data);
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `[getActiveMissionRoute]: Unexpected error occurred while fetching the active mission route for vin: ${vin}`,
            loggableError
        );
    }
};

/**
 * `GET /api/v2/missions/active/{vin}` -> `MissionFE | null`. 204 ("no active mission") is checked
 * before `response.data`: axios leaves a body-less response as `''` and the mapper would throw. 404
 * stays an error. Route topped up here, not in `onCacheEntryAdded`, which is skipped in DEV.
 */
const getActiveMissionQueryFn = async (vin: string): Promise<{ data: MissionFE | null }> => {
    try {
        const response = await missionsApi.getActiveMissionByVin(vin, correlationHeaders());
        if (response.status === 204) {
            return { data: null };
        }
        const mission = missionFromRestV2(response.data);
        // Strict `=== undefined`: `missionFromRestV2` sets `undefined`, never `null`.
        if (mission.route === undefined) {
            try {
                const route = await getActiveMissionRoute(vin);
                if (route !== null) {
                    mission.route = route;
                }
            } catch {
                // Already logged. Return the mission route-less rather than failing it.
            }
        }
        return { data: mission };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `[getActiveMission]: Unexpected error occurred while fetching the active mission for vin: ${vin}`,
            loggableError
        );
    }
};

export const missionV3Api = createApi({
    reducerPath: 'missionV3',
    // All endpoints use `queryFn`, so the base query is never called. `fakeBaseQuery` ensures a
    // future endpoint that omits `queryFn` fails loudly instead of silently POSTing to `/graphql`.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    // Not what drives the identity-change refetch (that forces an `initiate` so it can read the
    // result); this is what lets mock mode refresh the panel after a mission command.
    tagTypes: ['Mission'],
    endpoints: builder => ({
        getActiveMission: builder.query<MissionFE | null, string>({
            queryFn: getActiveMissionQueryFn,
            providesTags: (_result, _error, vin) => [{ type: 'Mission', id: vin }],
        }),
        onMissionV3Subscription: builder.query<MissionV2 | null, string>({
            // Resolves synchronously with no request. This is what stops missions being queried
            // from GraphQL; the data comes from `getActiveMission` over REST.
            queryFn: () => ({ data: null }),
            onCacheEntryAdded: async (
                vin,
                { cacheDataLoaded, cacheEntryRemoved, dispatch, getState, updateCachedData }
            ) => {
                await cacheDataLoaded;
                if (import.meta.env.DEV) {
                    // No subscription in DEV, as before this migration. The mission and its route
                    // still load over REST through the top-up in `getActiveMissionQueryFn`.
                    await cacheEntryRemoved;
                } else {
                    /** The mission whose route has already been asked for, within this entry's life. */
                    let routeRequestedFor: string | undefined;
                    /** The newest pushed mission id, so a superseded push cannot overwrite it. */
                    let latestPushedMissionId: string | undefined;

                    const readCachedMission = () =>
                        missionV3Api.endpoints.getActiveMission.select(vin)(getState()).data;

                    /**
                     * A route can exceed the SNS message size limit and never changes after
                     * creation, so pushes omit it. Secondary path: at most one request per mission
                     * per cache entry, guard released only for outcomes that are not conclusive.
                     */
                    const fetchMissingRoute = async (missionId: string): Promise<void> => {
                        if (routeRequestedFor === missionId) {
                            return;
                        }
                        routeRequestedFor = missionId;
                        try {
                            const route = await getActiveMissionRoute(vin);
                            if (route == null) {
                                // 204 — conclusive. Keep the guard so we do not re-ask.
                                return;
                            }
                            const cached = readCachedMission();
                            if (cached == null || cached.id !== missionId) {
                                // NOT conclusive: the entry has not caught up, or a newer mission
                                // took over. Release the guard so a later push can retry.
                                routeRequestedFor = undefined;
                                return;
                            }
                            if (cached.route !== undefined) {
                                // Already filled — conclusive.
                                return;
                            }
                            dispatch(
                                missionV3Api.util.updateQueryData('getActiveMission', vin, draft => {
                                    // Re-checked here: `select` read the store before this dispatch.
                                    if (draft != null && draft.id === missionId && draft.route === undefined) {
                                        draft.route = route;
                                    }
                                })
                            );
                        } catch {
                            // Already logged by getActiveMissionRoute. Allow a later push to retry.
                            routeRequestedFor = undefined;
                        }
                    };

                    /**
                     * Tops up an identity change with what a push does not carry. The caller already
                     * wrote the pushed mission, so if the forced read disagrees (previous mission,
                     * 204, rejection) the push is written back, guarded by `latestPushedMissionId`.
                     */
                    const refreshMission = async (mission: MissionV2): Promise<void> => {
                        let refreshed: MissionFE | null = null;
                        try {
                            // `forceRefetch` to re-read this vin now; `subscribe: false` because the
                            // component's own `useGetActiveMissionQuery` owns the subscription.
                            refreshed = await dispatch(
                                missionV3Api.endpoints.getActiveMission.initiate(vin, {
                                    subscribe: false,
                                    forceRefetch: true,
                                })
                            ).unwrap();
                        } catch {
                            // Already logged; the component's hook carries the error state.
                        }

                        if (refreshed != null && refreshed.id === mission.id) {
                            if (refreshed.route === undefined) {
                                await fetchMissingRoute(mission.id);
                            }
                            return;
                        }

                        if (latestPushedMissionId !== mission.id) {
                            // A newer mission has been pushed since; it owns the cache now.
                            return;
                        }
                        await dispatch(
                            missionV3Api.util.upsertQueryData('getActiveMission', vin, missionFromLiveV2(mission))
                        );
                        if (readCachedMission()?.route === undefined) {
                            await fetchMissingRoute(mission.id);
                        }
                    };

                    const connection = client
                        .graphql({ query: onUpdateActiveMissionByVin, variables: { vin } })
                        .subscribe({
                            next: ({ data }) => {
                                if (!data.onUpdateActiveMissionByVin) {
                                    console.error(
                                        '[onMissionV3Subscription]: Please make sure all the attributes are returned in the mutation'
                                    );
                                    return;
                                }
                                const payload = data.onUpdateActiveMissionByVin.payload;
                                const hasMission = 'id' in payload && payload.id != null;
                                if (!hasMission) {
                                    latestPushedMissionId = undefined;
                                    updateCachedData(() => null);
                                    // `() => null`, never a recipe returning `undefined`: a `null`
                                    // cache value is not draftable, so the return value is patched
                                    // in as-is and `undefined` would read as "not loaded".
                                    dispatch(missionV3Api.util.updateQueryData('getActiveMission', vin, () => null));
                                    return;
                                }
                                const mission = parseMission(extractResult<MissionV2>(payload, 'Mission not found'));
                                if (mission.vin !== vin) {
                                    return;
                                }
                                latestPushedMissionId = mission.id;
                                // Keeps this entry's own state coherent, same as `getVehicleByVINV2`.
                                updateCachedData(() => mission);

                                /* Patches `getActiveMission` (REST, `MissionFE`) from this AppSync
                                   entry's lifecycle: it is the only live source and the REST entry
                                   is the only one anyone reads. Same as vehiclesV2.ts. */
                                const cached = readCachedMission();
                                if (cached != null && cached.id === mission.id) {
                                    dispatch(
                                        missionV3Api.util.updateQueryData('getActiveMission', vin, draft => {
                                            if (draft != null) {
                                                applyMissionLiveUpdate(draft, mission);
                                            }
                                        })
                                    );
                                    // Reads the REST cache, never the push: `applyMissionLiveUpdate`
                                    // never writes `route`, so a pushed route must not suppress this.
                                    if (cached.route === undefined) {
                                        void fetchMissingRoute(mission.id);
                                    }
                                    return;
                                }

                                // Identity changed (new mission, or nothing cached). Write the push
                                // first so the operator sees the announced mission, then force the
                                // REST read to enrich it. `upsertQueryData` creates a missing entry.
                                void (async () => {
                                    await dispatch(
                                        missionV3Api.util.upsertQueryData(
                                            'getActiveMission',
                                            vin,
                                            missionFromLiveV2(mission)
                                        )
                                    );
                                    await refreshMission(mission);
                                })();
                            },
                            error: error => console.warn(error),
                        });
                    cacheEntryRemoved
                        .then(() => {
                            connection.unsubscribe();
                        })
                        .catch(error => console.error(error));
                }
            },
        }),
    }),
});

export const { useGetActiveMissionQuery, useLazyGetActiveMissionQuery, useOnMissionV3SubscriptionQuery } = missionV3Api;
